import React, { useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import ConstructionBanner from '../components/ConstructionBanner';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

// Payment Link (pre-created, works without Cloud Function)
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/7sYaEQ7mb4z46bB46O6wE00';

// Cloud Function URL — set after Firebase Functions are deployed
const CHECKOUT_SESSION_FN = 'https://us-central1-football-for-nerds.cloudfunctions.net/createCheckoutSession';

const FREE_FEATURES = [
  'Player stat leaders — QB, RB, WR, TE, all defensive positions',
  'Nerd Rating (NR) — positional rankings for all skill positions',
  'Team advanced stats — EPA/play, success rate, explosive rate',
  'Team standings & schedules',
  'Individual team pages — roster, news, schedule',
  'Metrics Glossary — plain-English explanations',
  'NFL Draft & College pages (coming soon)',
];

const NERDS_PLUS_FEATURES = [
  'EPA per play — for every player, every position',
  'Success rate & explosive play rate at player level',
  'QB Situational Splits — shotgun, pressure, down, quarter, game script',
  'Drive-Level EPA Explorer — filter by starting field position',
  'Red Zone Tendencies — pass/run rates, TD%, EPA by team',
  'CPOE (Completion % Over Expected)',
  'aDOT — Average Depth of Target per receiver',
  'QB Pressure Rate — sacks + QB hits per dropback',
  'Player card advanced stats — full analytics profile',
  'Priority access to all new premium features',
  'Support independent NFL analytics',
];

function CheckIcon() {
  return <Box sx={{ color: '#2ea84b', fontSize: '1rem', fontWeight: 900 }}>✓</Box>;
}

export default function PricingPage() {
  const { currentUser, isBasic, isPremium, isAdmin, setAuthModalOpen } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!currentUser) {
      setAuthModalOpen(true);
      return;
    }
    setLoading(true);
    try {
      // Try Cloud Function first (attaches Firebase UID for instant webhook matching)
      const resp = await fetch(CHECKOUT_SESSION_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: currentUser.uid, email: currentUser.email }),
      });
      if (resp.ok) {
        const { url } = await resp.json();
        if (url) { window.location.href = url; return; }
      }
    } catch (_) {
      // Cloud Function not deployed yet — fall through to Payment Link
    }
    // Fallback: Payment Link (no UID attached; webhook matches by email/customer)
    window.location.href = STRIPE_PAYMENT_LINK;
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent' }}>
      <ConstructionBanner />
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{
          textAlign: 'center', mb: 6,
          pt: 2,
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(46,168,75,0.07) 0%, transparent 70%)',
        }}>
          <Chip label="💰 Pricing" size="small"
            sx={{ mb: 2, bgcolor: 'rgba(46,168,75,0.10)', color: '#3ec45e', border: '1px solid rgba(46,168,75,0.25)', fontWeight: 700, fontSize: '0.75rem' }} />
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 1.5, letterSpacing: '-0.03em' }}>
            Simple, Honest Pricing
          </Typography>
          <Typography variant="h6" sx={{ color: '#7a9ab8', fontWeight: 400, maxWidth: 540, mx: 'auto', letterSpacing: '-0.01em', lineHeight: 1.7 }}>
            Free forever for the fundamentals. Nerds+ unlocks the analytics that win fantasy leagues and bar arguments.
          </Typography>
        </Box>

        {/* Pricing cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, maxWidth: 900, mx: 'auto', mb: 6 }}>

          {/* Free tier */}
          <Card sx={{
            background: 'linear-gradient(135deg, rgba(10,20,36,0.85) 0%, rgba(6,14,26,0.85) 100%)',
            border: '1px solid rgba(42,69,96,0.6)', borderRadius: 3,
            backdropFilter: 'blur(8px)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Free</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: '#e8f0f8' }}>$0</Typography>
                <Typography sx={{ color: '#6a8aa8' }}>/ forever</Typography>
              </Box>
              <Button fullWidth variant="outlined" color="primary"
                onClick={() => { if (isBasic) setAuthModalOpen(true); }}
                disabled={!isBasic}
                sx={{ mb: 3, textTransform: 'none', fontWeight: 700,
                  ...(isBasic ? {
                    borderColor: '#2ea84b', color: '#2ea84b',
                    '&:hover': { borderColor: '#2ea84b', backgroundColor: 'rgba(46,168,75,0.1)' },
                  } : {}),
                }}>
                {isBasic ? 'Sign Up Free →' : '✓ Your Current Plan'}
              </Button>
              <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', mb: 2 }} />
              <List dense disablePadding>
                {FREE_FEATURES.map(f => (
                  <ListItem key={f} disableGutters sx={{ py: 0.4 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon /></ListItemIcon>
                    <ListItemText primary={f} primaryTypographyProps={{ sx: { fontSize: '0.83rem', color: '#c0d4e8' } }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Nerds+ tier */}
          <Card sx={{
            background: 'linear-gradient(135deg, rgba(10,22,36,0.92) 0%, rgba(6,16,28,0.92) 100%)',
            border: '1px solid rgba(46,168,75,0.45)',
            borderRadius: 3,
            position: 'relative',
            boxShadow: '0 0 48px rgba(46,168,75,0.12), inset 0 1px 0 rgba(46,168,75,0.15)',
            backdropFilter: 'blur(8px)',
          }}>
            <Box sx={{
              position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)',
              bgcolor: '#2ea84b', color: '#000', fontWeight: 800, fontSize: '0.72rem',
              px: 2, py: 0.4, borderRadius: '0 0 8px 8px', letterSpacing: '0.05em',
            }}>
              MOST POPULAR
            </Box>
            <CardContent sx={{ p: 3, pt: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Nerds+</Typography>
                <Chip label="⚡ Analytics Tier" color="secondary" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: '#e8f0f8' }}>$6</Typography>
                <Typography sx={{ color: '#6a8aa8' }}>/ month</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.78rem', color: 'rgba(245,166,35,0.4)', mb: 2 }}>
                ~$0.35/game · Cancel anytime
              </Typography>

              {isPremium || isAdmin ? (
                <Button fullWidth variant="contained" color="success" disabled
                  sx={{ mb: 3, textTransform: 'none', fontWeight: 700 }}>
                  ✓ {isAdmin ? 'Admin Access' : 'Nerds+ Active'}
                </Button>
              ) : (
                <Button fullWidth variant="contained" color="primary" onClick={handleUpgrade}
                  disabled={loading}
                  sx={{ mb: 3, textTransform: 'none', fontWeight: 700, py: 1.25, fontSize: '1rem',
                    boxShadow: '0 4px 20px rgba(46,168,75,0.4)',
                    '&:hover': { boxShadow: '0 6px 28px rgba(46,168,75,0.5)' } }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : (currentUser ? 'Upgrade to Nerds+ →' : 'Get Started — $6/mo')}
                </Button>
              )}

              <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', mb: 2 }} />
              <Typography sx={{ fontSize: '0.72rem', color: '#6a8aa8', mb: 1.5, fontWeight: 700 }}>
                EVERYTHING IN FREE, PLUS:
              </Typography>
              <List dense disablePadding>
                {NERDS_PLUS_FEATURES.map(f => (
                  <ListItem key={f} disableGutters sx={{ py: 0.4 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon /></ListItemIcon>
                    <ListItemText primary={f} primaryTypographyProps={{ sx: { fontSize: '0.83rem', color: '#c0d4e8' } }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* FAQ */}
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>Frequently Asked Questions</Typography>
          {[
            { q: 'Why $6/month?', a: 'It covers server costs, data processing, and keeps the free tier genuinely free. We\'d rather charge a fair price than paywall basic stats.' },
            { q: 'What data powers the advanced stats?', a: 'nflverse play-by-play data — the same open-source dataset used by most public NFL analytics researchers. Every play since 1999, updated weekly during the season.' },
            { q: 'Can I cancel anytime?', a: 'Yes. Stripe handles billing — cancel with one click, no friction.' },
            { q: 'When will Stripe payments be live?', a: 'Very soon — we\'re finishing the integration now. Join the waitlist to get notified the moment it goes live and lock in the founding member rate.' },
            { q: 'Is the free tier really free forever?', a: 'Yes. Player leaders, standings, team pages, and the Nerd Rating are permanently free. We grow by making the free tier genuinely useful.' },
          ].map(({ q, a }) => (
            <Box key={q} sx={{
              mb: 2, p: 2.5, borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(10,20,36,0.7) 0%, rgba(6,14,26,0.7) 100%)',
              border: '1px solid rgba(26,46,68,0.7)',
              backdropFilter: 'blur(6px)',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'rgba(46,168,75,0.2)' },
            }}>
              <Typography sx={{ fontWeight: 700, mb: 0.75, color: '#d8eaf8', letterSpacing: '-0.01em' }}>{q}</Typography>
              <Typography variant="body2" sx={{ color: '#7a9ab8', lineHeight: 1.75, letterSpacing: '-0.005em' }}>{a}</Typography>
            </Box>
          ))}
        </Box>

        {/* Waitlist CTA */}
        <Box sx={{
          textAlign: 'center', mt: 6, p: { xs: 3, md: 5 }, borderRadius: 3,
          background: 'radial-gradient(ellipse 80% 100% at 50% 50%, rgba(46,168,75,0.08) 0%, transparent 70%), linear-gradient(135deg, rgba(10,22,38,0.6) 0%, rgba(6,14,26,0.6) 100%)',
          border: '1px solid rgba(46,168,75,0.2)',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Not ready yet?</Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 2 }}>
            Join the waitlist — get notified when Nerds+ launches and lock in the founding member rate.
          </Typography>
          <Button variant="contained" color="secondary" href="/#waitlist-section" size="large"
            sx={{ fontWeight: 700, textTransform: 'none', px: 4 }}>
            Join the Waitlist — Free
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
